import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, TablePagination, Snackbar, Alert,
  Select, MenuItem, FormControl, InputLabel, Typography
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { axiosInstances } from "./apiConfig";

export default function DataUploadUI() {

  const [uploads, setUploads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [pendingDeletes, setPendingDeletes] = useState({});
  const deleteTimers = useRef({});
  const [file, setFile] = useState(null);

  const loadUploads = async (pageNum = page, size = rowsPerPage) => {
    try {
      const res = await axiosInstances.datauploadApi.get(`/?page=${pageNum}&size=${size}`);
      const payload = res.data.data || {};
      setUploads(payload.data || []);
      setTotal(payload.total || 0);
    } catch (err) {
      console.error("Error fetching data uploads", err);
    }
  };

  useEffect(() => { loadUploads(); }, [page, rowsPerPage]);

  const handleOpen = (upload = {}) => {
    const transformedUpload = {
      ...upload,
      processedAt: upload.processedAt
        ? convertToInputDateFormat(upload.processedAt)
        : new Date().toISOString().split("T")[0],
    };
    setForm(transformedUpload);
    setEditing(!!upload.id);
    setOpen(true);
  };

  function formatDate(inputDate) {
    if (!inputDate) return "";
    const [year, month, day] = inputDate.split("-");
    return `${day}-${month}-${year}`;
  }

  function convertToInputDateFormat(dateStr) {
    if (!dateStr || !dateStr.includes("-")) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  const handleClose = () => { setForm({}); setOpen(false); setFile(null);};

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      // if (!token) {
      //   setSnackbar({ open: true, message: "Unauthorized - please log in as admin", severity: "error" });
      //   return;
      // }

      if (file) {
        // ✅ Validate file type
        const allowedTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          "application/vnd.ms-excel", // .xls
          "text/csv", // .csv
        ];

        if (!allowedTypes.includes(file.type)) {
          setSnackbar({ open: true, message: "Only Excel or CSV files are allowed!", severity: "error" });
          return;
        }

        const formData = new FormData();
        formData.append("file", file);

        // ✅ Upload data file
        await axiosInstances.datauploadApi.post(`/uploadDataByFile`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setSnackbar({ open: true, message: "Data file uploaded successfully!", severity: "success" });

      } else {
        // ✅ Manual data submission
        const dataPayload = {
          ...form,
          processedAt: formatDate(form.processedAt),
        };

        const endpoint = editing ? `/update` : `/save`;

        await axiosInstances.datauploadApi.post(endpoint, dataPayload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const successMsg = editing ? "Data updated successfully!" : "Data saved successfully!";
        setSnackbar({ open: true, message: successMsg, severity: "success" });
      }

      // ✅ Post submission cleanup
      loadUploads();     // Reload data list
      handleClose();  // Close modal/dialog
      setFile(null);  // Reset file input
    } catch (err) {
      if (err.response?.status === 409) {
        setSnackbar({ open: true, message: "Duplicate data entry", severity: "error" });
      } else if (err.response?.status === 401) {
        setSnackbar({ open: true, message: "Unauthorized - please log in as admin", severity: "error" });
      } else {
        setSnackbar({ open: true, message: "Something went wrong!", severity: "error" });
      }
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };


  const handleDeleteClick = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));
    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances.datauploadApi.post(`/delete`, { id });
        loadUploads();
      } catch (err) {
        console.error("Delete failed", err);
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndoClick = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }
    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Upload Management</Typography>
      <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
        Add DataUpload
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>File Name</TableCell>
              <TableCell>File Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Uploaded By</TableCell>
              <TableCell>Record Count</TableCell>
              <TableCell>Processed At</TableCell>
              <TableCell>Source Module</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell>Error Message</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uploads.map((upload, index) => (
              <TableRow key={upload.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{upload.id}</TableCell>
                <TableCell>{upload.fileName}</TableCell>
                <TableCell>{upload.fileType}</TableCell>
                <TableCell>{upload.uploadStatus}</TableCell>
                <TableCell>{upload.uploadedBy}</TableCell>
                <TableCell>{upload.recordCount}</TableCell>
                <TableCell>{upload.processedAt}</TableCell>
                <TableCell>{upload.sourceModule}</TableCell>
                <TableCell>{upload.remarks}</TableCell>
                <TableCell>{upload.errorMessage}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpen(upload)}>
                    <Edit />
                  </IconButton>
                  {pendingDeletes[upload.id] ? (
                    <IconButton color="warning" onClick={() => handleUndoClick(upload.id)}>
                      <Undo />
                    </IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDeleteClick(upload.id)}>
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {uploads.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center">No data uploads found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) => `Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`}
        />
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editing ? "Edit DataUpload" : "Add DataUpload"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="File Name" name="fileName" value={form.fileName || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="File Type" name="fileType" value={form.fileType || ""} onChange={handleChange} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select name="uploadStatus" value={form.uploadStatus || ""} onChange={handleChange}>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="PROCESSING">Processing</MenuItem>
              <MenuItem value="SUCCESS">Success</MenuItem>
              <MenuItem value="FAILED">Failed</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="dense" label="Uploaded By" name="uploadedBy" value={form.uploadedBy || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Record Count" name="recordCount" type="number" value={form.recordCount || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Processed At" name="processedAt" type="date" value={form.processedAt || new Date().toISOString().split("T")[0]} onChange={handleChange} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" label="Source Module" name="sourceModule" value={form.sourceModule || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Remarks" name="remarks" value={form.remarks || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Error Message" name="errorMessage" value={form.errorMessage || ""} onChange={handleChange} />
          {!editing && (
            <>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
